import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import proj4 from "proj4";
import {
  getComunasByMunicipio,
  getDepartamentos,
} from "../../shared/data/colombiaLocations";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";
import {
  cargarBorradorFormulario,
  guardarBorradorFormulario,
  limpiarBorradorFormulario,
} from "../../utils/proyectoFormBorrador";

proj4.defs(
  "EPSG:3116",
  "+proj=tmerc +lat_0=4 +lon_0=-73 +k=0.9992 +x_0=1000000 +y_0=1000000 +ellps=GRS80 +units=m +no_defs +type=crs",
);

const steps = [
  "Detalle del servicio",
  "Datos del solicitante",
  "Datos del predio",
  "Uso y actividad economica",
  "Infraestructura electrica",
  "Observaciones",
];

const nivelesTension = [
  { value: "Nivel 2", label: "Nivel 2. Tension mayor o igual a 1 kV y menor a 30 kV" },
  { value: "Nivel 3", label: "Nivel 3. Tension mayor o igual a 30 kV y menor a 57.5 kV" },
];
const municipiosPermitidos = ["Puerto Tejada", "Yumbo", "Cali"];
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Documentos en solicitud_servicio al crear la solicitud. */
const TIPO_DOC_IDENTIDAD_SOLICITUD = "solicitud_documento_identidad";
const TIPO_DOC_ACREDITACION_USO_ESPECIAL = "solicitud_acreditacion_uso_especial";
const EXTENSIONES_DOC_SOLICITUD = ".pdf,.jpg,.jpeg,.png,.webp";

const getToken = () => {
  const encoded = localStorage.getItem("authToken");
  return encoded ? atob(encoded) : "";
};

const crearFormularioInicial = () => ({
  codigo: "",
  nombre: "",
  tipo_proyecto: "Proyecto Nuevo",
  nivel_tension: "",
  fecha_estimada_operacion: "",
  numero_usuarios: "0",
  carga_total_solicitada_kva: "",
  tipo_subestacion: "Exterior",
  nodo_referencia: "",
  nombre_solicitante: "",
  tipo_documento: "Cedula de ciudadania",
  documento_solicitante: "",
  tipo_persona: "Natural",
  tipo_persona_juridica: "Privada",
  tipo_via: "Avenida",
  via_principal: "",
  bis: "Bis",
  numero_via_secundaria: "",
  orientacion: "Oeste",
  numero_predio: "",
  numero_interior: "",
  correo_notificacion: "",
  confirmar_email: "",
  telefono_solicitante: "",
  confirmar_celular: "",
  telefono_fijo: "",
  autoriza_notificacion: "Si",
  es_propietario: "Si",
  zona: "Urbana",
  solicitante_municipio: "",
  solicitante_departamento: "Valle Del Cauca",
  solicitante_comuna: "",
  predio_municipio: "",
  predio_departamento: "Valle Del Cauca",
  predio_comuna: "",
  direccion_predio: "",
  latitud: "",
  longitud: "",
  altura: "",
  magna_este: "",
  magna_norte: "",
  tipo_uso: "Comercial",
  estrato: "2",
  red_cercana: "",
  distancia_red: "",
  nodo_mas_cercano: "",
  nodo_adicional: "",
  observaciones: "",
  responsable_emcali: "",
  fecha_radicado: "",
});

type FormState = ReturnType<typeof crearFormularioInicial>;

type FormCrearProyectoProps = {
  publicMode?: boolean;
  cancelRoute?: string;
  successRoute?: string;
};

export default function FormCrearProyecto({
  publicMode = false,
  cancelRoute = "/proyectos-panel",
  successRoute = "/proyectos-panel",
}: FormCrearProyectoProps) {
  const navigate = useNavigate();
  const borradorInicial = React.useMemo(() => cargarBorradorFormulario(publicMode), [publicMode]);
  const [step, setStep] = React.useState(() => borradorInicial?.step ?? 0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [ok, setOk] = React.useState<string | null>(null);
  const [borradorActivo, setBorradorActivo] = React.useState(!!borradorInicial);
  const [lastEditedCoord, setLastEditedCoord] = React.useState<"geo" | "magna" | null>(null);
  const [archivoIdentidad, setArchivoIdentidad] = React.useState<File | null>(null);
  const [archivoUsoEspecial, setArchivoUsoEspecial] = React.useState<File | null>(null);
  const [form, setForm] = React.useState<FormState>(() => {
    if (!borradorInicial?.form) return crearFormularioInicial();
    return { ...crearFormularioInicial(), ...borradorInicial.form } as FormState;
  });

  React.useEffect(() => {
    const cargarCodigo = async () => {
      try {
        const response = await fetch(
          `${getProyectosApiBaseUrl()}/siguiente-codigo`,
          {
            headers: {
              "Content-Type": "application/json",
              ...(publicMode ? {} : { Authorization: `Bearer ${getToken()}` }),
            },
          },
        );
        if (!response.ok) return;
        const data = await response.json();
        if (data?.codigo) {
          setForm((prev) => ({ ...prev, codigo: data.codigo }));
        }
      } catch {
        // Si falla, no bloquea el flujo; backend vuelve a generar al guardar.
      }
    };
    cargarCodigo();
  }, [publicMode]);

  const pasoObservaciones = steps.length - 1;

  /** Borrador solo en el paso Observaciones; no persiste pasos anteriores en localStorage. */
  React.useEffect(() => {
    if (step !== pasoObservaciones) return;
    const timer = window.setTimeout(() => {
      guardarBorradorFormulario(publicMode, { step, form: form as Record<string, unknown> });
      setBorradorActivo(true);
    }, 500);
    return () => window.clearTimeout(timer);
  }, [form, step, publicMode, pasoObservaciones]);

  const onChange =
    (key: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const setCoordValue =
    (key: "latitud" | "longitud" | "magna_este" | "magna_norte", source: "geo" | "magna") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLastEditedCoord(source);
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  React.useEffect(() => {
    if (lastEditedCoord !== "geo") return;
    const lat = Number(form.latitud);
    const lon = Number(form.longitud);
    if (Number.isNaN(lat) || Number.isNaN(lon)) return;
    const [x, y] = proj4("EPSG:4326", "EPSG:3116", [lon, lat]);
    setForm((prev) => ({ ...prev, magna_este: x.toFixed(3), magna_norte: y.toFixed(3) }));
    setLastEditedCoord(null);
  }, [form.latitud, form.longitud, lastEditedCoord]);

  React.useEffect(() => {
    if (lastEditedCoord !== "magna") return;
    const east = Number(form.magna_este);
    const north = Number(form.magna_norte);
    if (Number.isNaN(east) || Number.isNaN(north)) return;
    const [lon, lat] = proj4("EPSG:3116", "EPSG:4326", [east, north]);
    setForm((prev) => ({ ...prev, latitud: lat.toFixed(8), longitud: lon.toFixed(8) }));
    setLastEditedCoord(null);
  }, [form.magna_este, form.magna_norte, lastEditedCoord]);

  const requiredByStep = () => {
    if (step === 0) {
      const nodoOk = /^\d{7}$/.test(form.nodo_referencia);
      return form.codigo && form.nombre && form.nivel_tension && nodoOk;
    }
    if (step === 1) {
      const email = form.correo_notificacion.trim().toLowerCase();
      const emailConfirm = form.confirmar_email.trim().toLowerCase();
      return (
        form.nombre_solicitante &&
        form.tipo_documento &&
        form.documento_solicitante &&
        email &&
        emailConfirm &&
        emailRegex.test(email) &&
        email === emailConfirm &&
        form.telefono_solicitante &&
        form.confirmar_celular &&
        form.telefono_solicitante === form.confirmar_celular &&
        (form.tipo_persona !== "Juridica" || !!form.tipo_persona_juridica) &&
        !!archivoIdentidad
      );
    }
    if (step === 2) {
      return form.predio_departamento && form.predio_municipio && form.predio_comuna;
    }
    if (step === 3) {
      if (!form.tipo_uso) return false;
      if (form.tipo_uso === "Residencial") return !!form.estrato;
      if (form.tipo_uso === "Especial") return !!archivoUsoEspecial;
      return true;
    }
    if (step === 4) {
      if (form.red_cercana !== "Si" && form.red_cercana !== "No") return false;
      if (form.red_cercana === "Si") {
        return !!String(form.distancia_red || "").trim() && !!String(form.nodo_mas_cercano || "").trim();
      }
      return true;
    }
    return true;
  };

  const onRedCercanaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      red_cercana: value,
      ...(value === "No"
        ? { distancia_red: "", nodo_mas_cercano: "", nodo_adicional: "" }
        : {}),
    }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const finalizarYCrear = () => {
    if (step !== pasoObservaciones) return;
    void onSubmit({ preventDefault: () => {} } as React.FormEvent);
  };

  const descartarBorrador = () => {
    limpiarBorradorFormulario(publicMode);
    setBorradorActivo(false);
    setForm(crearFormularioInicial());
    setStep(0);
    setArchivoUsoEspecial(null);
    setOk(null);
    setError(null);
  };

  const onTipoUsoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      tipo_uso: value,
      estrato: value === "Residencial" ? prev.estrato || "2" : "",
    }));
    if (value !== "Especial") setArchivoUsoEspecial(null);
  };

  const departamentos = React.useMemo(
    () =>
      getDepartamentos().filter(
        (d) => d.toLowerCase().replace(/\s+/g, " ").trim() === "valle del cauca",
      ),
    [],
  );
  const municipiosSolicitante = React.useMemo(
    () => municipiosPermitidos,
    [],
  );
  const comunasSolicitante = React.useMemo(
    () => getComunasByMunicipio(form.solicitante_municipio),
    [form.solicitante_municipio],
  );
  const municipiosPredio = React.useMemo(
    () => municipiosPermitidos,
    [],
  );
  const comunasPredio = React.useMemo(
    () => getComunasByMunicipio(form.predio_municipio),
    [form.predio_municipio],
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setOk(null);
      const emailNormalizado = form.correo_notificacion.trim().toLowerCase();
      if (!emailRegex.test(emailNormalizado)) {
        throw new Error("Ingrese un correo valido.");
      }
      const user = localStorage.getItem("user");
      const response = await fetch(`${getProyectosApiBaseUrl()}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(publicMode ? {} : { Authorization: `Bearer ${getToken()}` }),
        },
        body: JSON.stringify({
          codigo: form.codigo,
          nombre: form.nombre,
          solicitante: form.nombre_solicitante,
          tipo_proyecto: form.tipo_proyecto,
          nivel_tension: form.nivel_tension,
          capacidad_mva: form.carga_total_solicitada_kva
            ? String((Number(form.carga_total_solicitada_kva) || 0) / 1000)
            : "",
          municipio: form.predio_municipio,
          responsable_emcali: form.responsable_emcali || form.nombre_solicitante,
          correo_notificacion: emailNormalizado,
          fecha_radicado: form.fecha_radicado || undefined,
          created_by: user ? atob(user) : "sistema",
          solicitud_resumen_json: JSON.stringify(form),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "No fue posible crear el proyecto.");

      const proyectoId = data?.id;
      const subirDocSolicitud = async (archivo: File, tipo: string, etiqueta: string) => {
        const fd = new FormData();
        fd.append("archivo", archivo);
        fd.append("etapa_key", "solicitud_servicio");
        const uploadRes = await fetch(
          `${getProyectosApiBaseUrl()}/${proyectoId}/documentos/revision/${tipo}`,
          {
            method: "POST",
            headers: publicMode ? {} : { Authorization: `Bearer ${getToken()}` },
            body: fd,
          },
        );
        if (!uploadRes.ok) {
          throw new Error(
            `El proyecto se creó, pero no se pudo cargar ${etiqueta}. Suba el archivo desde gestión o el portal de corrección.`,
          );
        }
      };
      if (proyectoId && archivoIdentidad) {
        await subirDocSolicitud(
          archivoIdentidad,
          TIPO_DOC_IDENTIDAD_SOLICITUD,
          "el documento de identidad",
        );
      }
      if (form.tipo_uso === "Especial" && archivoUsoEspecial && proyectoId) {
        await subirDocSolicitud(
          archivoUsoEspecial,
          TIPO_DOC_ACREDITACION_USO_ESPECIAL,
          "el documento que acredita el uso especial",
        );
      }

      limpiarBorradorFormulario(publicMode);
      setBorradorActivo(false);
      setOk("Proyecto creado correctamente.");
      setTimeout(() => navigate(successRoute), 700);
    } catch (err: any) {
      setError(err.message || "Error al crear proyecto.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        Crear Proyecto
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Flujo guiado con ayudas por campo y conversion bidireccional Lat/Lon - MAGNA SIRGAS.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }}>{ok}</Alert>}
      {borradorActivo && !ok && step === pasoObservaciones && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Borrador de observaciones guardado en este navegador. El proyecto solo se crea en el servidor al
          pulsar «Finalizar y crear».{" "}
          <Button size="small" variant="text" onClick={descartarBorrador} sx={{ ml: 0.5, p: 0, minWidth: 0 }}>
            Descartar borrador
          </Button>
        </Alert>
      )}

      <Stepper activeStep={step} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box
        component="form"
        onSubmit={handleFormSubmit}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          const tag = (e.target as HTMLElement).tagName;
          if (tag === "TEXTAREA") return;
          e.preventDefault();
        }}
      >
        {step === 0 && (
          <Stack spacing={1.5}>
            <TextField
              label="Codigo del proyecto (automatico)"
              value={form.codigo}
              InputProps={{ readOnly: true }}
              helperText="Ayuda: Se genera automaticamente con formato UPM-consecutivo."
            />
            <TextField label="Nombre del proyecto *" required value={form.nombre} onChange={onChange("nombre")} />
            <TextField select label="Tipo de proyecto" value={form.tipo_proyecto} onChange={onChange("tipo_proyecto")} helperText="Ayuda: seleccion segun clase del servicio.">
              <MenuItem value="Proyecto Nuevo">Proyecto Nuevo</MenuItem>
              <MenuItem value="Aumento de capacidad">Aumento de capacidad</MenuItem>
              <MenuItem value="Disminucion de capacidad">Disminucion de capacidad</MenuItem>
              <MenuItem value="Provisional de obra">Provisional de obra</MenuItem>
              <MenuItem value="Modificacion de datos basicos">Modificacion de datos basicos</MenuItem>
              <MenuItem value="Cambio de nivel de tension en el punto de conexion">
                Cambio de nivel de tension en el punto de conexion
              </MenuItem>
              <MenuItem value="Cambio de nivel de tension en la medida">
                Cambio de nivel de tension en la medida
              </MenuItem>
              <MenuItem value="Reubicacion de redes">Reubicacion de redes</MenuItem>
              <MenuItem value="Alumbrado publico con montaje de transformador">
                Alumbrado publico con montaje de transformador
              </MenuItem>
              <MenuItem value="Alumbrado Publico para adicionar a transformador existente">
                Alumbrado Publico para adicionar a transformador existente
              </MenuItem>
            </TextField>
            <TextField select label="Nivel de tension solicitado *" required value={form.nivel_tension} onChange={onChange("nivel_tension")}>
              {nivelesTension.map((n) => <MenuItem key={n.value} value={n.value}>{n.label}</MenuItem>)}
            </TextField>
            <TextField label="Fecha estimada entrada en operacion" type="date" InputLabelProps={{ shrink: true }} value={form.fecha_estimada_operacion} onChange={onChange("fecha_estimada_operacion")} />
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField label="Numero de usuarios" value={form.numero_usuarios} onChange={onChange("numero_usuarios")} fullWidth />
              <TextField label="Carga total solicitada (kVA)" value={form.carga_total_solicitada_kva} onChange={onChange("carga_total_solicitada_kva")} fullWidth />
            </Stack>
            <TextField select label="Tipo de subestacion" value={form.tipo_subestacion} onChange={onChange("tipo_subestacion")} helperText="Ayuda: Interior o Exterior segun infraestructura.">
              <MenuItem value="Interior">Interior</MenuItem>
              <MenuItem value="Exterior">Exterior</MenuItem>
            </TextField>
            <TextField
              label="Nodo de referencia *"
              required
              value={form.nodo_referencia}
              onChange={onChange("nodo_referencia")}
              inputProps={{ maxLength: 7, inputMode: "numeric", pattern: "[0-9]*" }}
              error={form.nodo_referencia !== "" && !/^\d{7}$/.test(form.nodo_referencia)}
              helperText={
                form.nodo_referencia !== "" && !/^\d{7}$/.test(form.nodo_referencia)
                  ? "Debe contener exactamente 7 numeros."
                  : "Ayuda: Ingrese exactamente 7 numeros."
              }
            />
          </Stack>
        )}

        {step === 1 && (
          <Stack spacing={1.5}>
            <FormControl>
              <FormLabel>Tipo de documento</FormLabel>
              <RadioGroup row value={form.tipo_documento} onChange={onChange("tipo_documento")}>
                <FormControlLabel value="Cedula de ciudadania" control={<Radio />} label="Cédula de ciudadania" />
                <FormControlLabel value="Cedula de extranjeria" control={<Radio />} label="Cédula de extranjería" />
                <FormControlLabel value="Nit" control={<Radio />} label="Nit" />
              </RadioGroup>
            </FormControl>
            <TextField label="Nombre del solicitante *" required value={form.nombre_solicitante} onChange={onChange("nombre_solicitante")} />
            <TextField
              label="Numero de documento *"
              required
              value={form.documento_solicitante}
              onChange={onChange("documento_solicitante")}
            />
            <Alert severity="info">IMPORTANTE: Con este numero de documento podra consultar el estado de su solicitud.</Alert>
            <FormControl>
              <FormLabel>Tipo de persona</FormLabel>
              <RadioGroup row value={form.tipo_persona} onChange={onChange("tipo_persona")}>
                <FormControlLabel value="Natural" control={<Radio />} label="Natural" />
                <FormControlLabel value="Juridica" control={<Radio />} label="Juridica" />
              </RadioGroup>
            </FormControl>
            {form.tipo_persona === "Juridica" && (
              <FormControl>
                <FormLabel>Tipo de persona juridica</FormLabel>
                <RadioGroup
                  row
                  value={form.tipo_persona_juridica}
                  onChange={onChange("tipo_persona_juridica")}
                >
                  <FormControlLabel value="Estatal" control={<Radio />} label="Estatal" />
                  <FormControlLabel value="Privada" control={<Radio />} label="Privada" />
                  <FormControlLabel value="Sin animo de lucro" control={<Radio />} label="Sin ánimo de lucro" />
                  <FormControlLabel value="Mixta" control={<Radio />} label="Mixta" />
                </RadioGroup>
              </FormControl>
            )}
            <Typography variant="h6">Direccion de correspondencia</Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField select label="Tipo de via" value={form.tipo_via} onChange={onChange("tipo_via")} fullWidth>
                <MenuItem value="Avenida">Avenida</MenuItem>
                <MenuItem value="Calle">Calle</MenuItem>
                <MenuItem value="Carrera">Carrera</MenuItem>
                <MenuItem value="Diagonal">Diagonal</MenuItem>
                <MenuItem value="Transversal">Transversal</MenuItem>
              </TextField>
              <TextField label="Via principal" value={form.via_principal} onChange={onChange("via_principal")} fullWidth />
              <TextField select label="Bis" value={form.bis} onChange={onChange("bis")} fullWidth>
                <MenuItem value="">-</MenuItem>
                <MenuItem value="Bis">Bis</MenuItem>
              </TextField>
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField label="Via secundaria" value={form.numero_via_secundaria} onChange={onChange("numero_via_secundaria")} fullWidth />
              <TextField select label="Orientacion" value={form.orientacion} onChange={onChange("orientacion")} fullWidth>
                <MenuItem value="Norte">Norte</MenuItem>
                <MenuItem value="Sur">Sur</MenuItem>
                <MenuItem value="Este">Este</MenuItem>
                <MenuItem value="Oeste">Oeste</MenuItem>
              </TextField>
              <TextField label="Numero" value={form.numero_predio} onChange={onChange("numero_predio")} fullWidth />
            </Stack>
            <TextField
              label="Numero interior/piso (Bloque, Torre, Apartamento, Casa #)"
              value={form.numero_interior}
              onChange={onChange("numero_interior")}
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField
                select
                label="Departamento"
                value={form.solicitante_departamento}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    solicitante_departamento: e.target.value,
                    solicitante_municipio: "",
                    solicitante_comuna: "",
                  }))
                }
                fullWidth
                disabled
              >
                {departamentos.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
              <TextField
                select
                label="Municipio"
                value={form.solicitante_municipio}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    solicitante_municipio: e.target.value,
                    solicitante_comuna: "",
                  }))
                }
                disabled={!form.solicitante_departamento}
                fullWidth
              >
                {municipiosSolicitante.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              <TextField
                select
                label="Comuna"
                value={form.solicitante_comuna}
                onChange={(e) => setForm((prev) => ({ ...prev, solicitante_comuna: e.target.value }))}
                disabled={!form.solicitante_municipio}
                fullWidth
              >
                {comunasSolicitante.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Stack>
            <Typography variant="h6">Datos de contacto</Typography>
            <TextField
              label="Correo del solicitante *"
              type="email"
              required
              value={form.correo_notificacion}
              onChange={onChange("correo_notificacion")}
              error={form.correo_notificacion !== "" && !emailRegex.test(form.correo_notificacion.trim())}
              helperText={
                form.correo_notificacion !== "" && !emailRegex.test(form.correo_notificacion.trim())
                  ? "Ingrese un correo valido."
                  : ""
              }
            />
            <TextField
              label="Confirmar email *"
              type="email"
              required
              value={form.confirmar_email}
              onChange={onChange("confirmar_email")}
              error={
                form.confirmar_email !== "" &&
                form.confirmar_email.trim().toLowerCase() !== form.correo_notificacion.trim().toLowerCase()
              }
              helperText={
                form.confirmar_email !== "" &&
                form.confirmar_email.trim().toLowerCase() !== form.correo_notificacion.trim().toLowerCase()
                  ? "Los correos no coinciden."
                  : ""
              }
            />
            <Alert severity="info">IMPORTANTE: A este email se enviara el codigo de seguridad para acceder a la consulta de tus solicitudes.</Alert>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField label="Celular *" required value={form.telefono_solicitante} onChange={onChange("telefono_solicitante")} fullWidth />
              <TextField
                label="Confirmar celular *"
                required
                value={form.confirmar_celular}
                onChange={onChange("confirmar_celular")}
                error={form.confirmar_celular !== "" && form.confirmar_celular !== form.telefono_solicitante}
                helperText={
                  form.confirmar_celular !== "" && form.confirmar_celular !== form.telefono_solicitante
                    ? "Los celulares no coinciden."
                    : ""
                }
                fullWidth
              />
              <TextField label="Telefono fijo" value={form.telefono_fijo} onChange={onChange("telefono_fijo")} fullWidth />
            </Stack>
            <FormControl>
              <FormLabel>Autoriza la notificacion mediante correo electronico?</FormLabel>
              <RadioGroup row value={form.autoriza_notificacion} onChange={onChange("autoriza_notificacion")}>
                <FormControlLabel value="Si" control={<Radio />} label="Si" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            <FormControl>
              <FormLabel>El solicitante es el propietario del predio?</FormLabel>
              <RadioGroup row value={form.es_propietario} onChange={onChange("es_propietario")}>
                <FormControlLabel value="Si" control={<Radio />} label="Si" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            <Alert severity="info">El documento debe estar en buena calidad y preferiblemente en formato PDF.</Alert>
            <Button variant="outlined" component="label">
              {archivoIdentidad ? "Cambiar documento de identidad" : "Cargar documento de identidad *"}
              <input
                type="file"
                hidden
                accept={EXTENSIONES_DOC_SOLICITUD}
                onChange={(e) => setArchivoIdentidad(e.target.files?.[0] || null)}
              />
            </Button>
            {archivoIdentidad ? (
              <Typography variant="body2">
                Archivo: <strong>{archivoIdentidad.name}</strong>
              </Typography>
            ) : (
              <Typography variant="caption" color="error">
                Debe cargar la fotocopia del documento de identidad del solicitante.
              </Typography>
            )}
            <TextField label="Responsable EMCALI" value={form.responsable_emcali} onChange={onChange("responsable_emcali")} />
            <TextField label="Fecha de solicitud" type="date" InputLabelProps={{ shrink: true }} value={form.fecha_radicado} onChange={onChange("fecha_radicado")} />
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={1.5}>
            <FormControl>
              <FormLabel>Zona</FormLabel>
              <RadioGroup row value={form.zona} onChange={onChange("zona")}>
                <FormControlLabel value="Urbana" control={<Radio />} label="Urbana" />
                <FormControlLabel value="Rural" control={<Radio />} label="Rural" />
              </RadioGroup>
            </FormControl>
            <TextField label="Direccion del predio" value={form.direccion_predio} onChange={onChange("direccion_predio")} />
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField
                select
                label="Departamento *"
                required
                value={form.predio_departamento}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    predio_departamento: e.target.value,
                    predio_municipio: "",
                    predio_comuna: "",
                  }))
                }
                fullWidth
                disabled
              >
                {departamentos.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
              <TextField
                select
                label="Municipio *"
                required
                value={form.predio_municipio}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    predio_municipio: e.target.value,
                    predio_comuna: "",
                  }))
                }
                disabled={!form.predio_departamento}
                fullWidth
              >
                {municipiosPredio.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              <TextField
                select
                label="Comuna *"
                required
                value={form.predio_comuna}
                onChange={(e) => setForm((prev) => ({ ...prev, predio_comuna: e.target.value }))}
                disabled={!form.predio_municipio}
                fullWidth
              >
                {comunasPredio.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Stack>
            <Divider />
            <Typography variant="subtitle1" fontWeight={600}>Geolocalizacion</Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField label="Latitud" value={form.latitud} onChange={setCoordValue("latitud", "geo")} fullWidth helperText="Ayuda: calcula automaticamente MAGNA si completas lat/lon." />
              <TextField label="Longitud" value={form.longitud} onChange={setCoordValue("longitud", "geo")} fullWidth />
              <TextField label="Altura (h)" value={form.altura} onChange={onChange("altura")} fullWidth />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField label="MAGNA SIRGAS Este (X)" value={form.magna_este} onChange={setCoordValue("magna_este", "magna")} fullWidth helperText="Ayuda: calcula automaticamente lat/lon si completas Este/Norte." />
              <TextField label="MAGNA SIRGAS Norte (Y)" value={form.magna_norte} onChange={setCoordValue("magna_norte", "magna")} fullWidth />
            </Stack>
          </Stack>
        )}

        {step === 3 && (
          <Stack spacing={1.5}>
            <TextField
              select
              required
              label="Tipo de uso"
              value={form.tipo_uso}
              onChange={onTipoUsoChange}
              helperText="Estrato solo aplica a uso residencial. Uso especial requiere documento de acreditación."
            >
              <MenuItem value="Residencial">Residencial</MenuItem>
              <MenuItem value="Comercial">Comercial</MenuItem>
              <MenuItem value="Industrial">Industrial</MenuItem>
              <MenuItem value="Oficial">Oficial</MenuItem>
              <MenuItem value="Especial">Especial</MenuItem>
            </TextField>

            {form.tipo_uso === "Residencial" && (
              <TextField
                select
                required
                label="Estrato socioeconomico"
                value={form.estrato}
                onChange={onChange("estrato")}
              >
                {["1", "2", "3", "4", "5", "6"].map((e) => (
                  <MenuItem key={e} value={e}>
                    {e}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {form.tipo_uso === "Especial" && (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Documento que acredita uso especial *
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Adjunte el soporte que certifica que el predio corresponde a un uso especial (PDF o imagen).
                </Typography>
                <Button variant="outlined" component="label" sx={{ mr: 1 }}>
                  {archivoUsoEspecial ? "Cambiar archivo" : "Seleccionar archivo"}
                  <input
                    type="file"
                    hidden
                    accept={EXTENSIONES_DOC_SOLICITUD}
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setArchivoUsoEspecial(file);
                      e.target.value = "";
                    }}
                  />
                </Button>
                {archivoUsoEspecial ? (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Archivo: <strong>{archivoUsoEspecial.name}</strong>
                  </Typography>
                ) : (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                    Debe cargar el documento para continuar.
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        )}

        {step === 4 && (
          <Stack spacing={1.5}>
            <FormControl required>
              <FormLabel>¿Hay red eléctrica cercana al predio?</FormLabel>
              <RadioGroup row value={form.red_cercana} onChange={onRedCercanaChange}>
                <FormControlLabel value="Si" control={<Radio />} label="Sí" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>

            {form.red_cercana === "Si" && (
              <Stack spacing={1.5}>
                <TextField
                  required
                  label="Distancia actual del predio a red (mts)"
                  value={form.distancia_red}
                  onChange={onChange("distancia_red")}
                  fullWidth
                  type="number"
                  inputProps={{ min: 0 }}
                />
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                  <TextField
                    required
                    label="Número de nodo más cercano"
                    value={form.nodo_mas_cercano}
                    onChange={onChange("nodo_mas_cercano")}
                    fullWidth
                    helperText="Nodo principal de referencia en la red."
                  />
                  <TextField
                    label="Número de nodo adicional (opcional)"
                    value={form.nodo_adicional}
                    onChange={onChange("nodo_adicional")}
                    fullWidth
                    helperText="Segundo nodo de referencia, si aplica."
                  />
                </Stack>
              </Stack>
            )}

            {form.red_cercana === "No" && (
              <Typography variant="body2" color="text.secondary">
                No se requiere distancia ni nodos cuando no hay red cercana.
              </Typography>
            )}
          </Stack>
        )}

        {step === 5 && (
          <Stack spacing={1.5}>
            <TextField
              label="Observaciones generales de la solicitud"
              value={form.observaciones}
              onChange={onChange("observaciones")}
              multiline
              minRows={5}
              helperText="Ayuda: informacion complementaria del punto de conexion."
            />
          </Stack>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => {
              if (step === 0) {
                limpiarBorradorFormulario(publicMode);
                navigate(cancelRoute);
              } else {
                setStep(step - 1);
              }
            }}
          >
            {step === 0 ? "Cancelar" : "Atras"}
          </Button>
          {step < pasoObservaciones ? (
            <Button
              type="button"
              variant="contained"
              onClick={() => setStep(step + 1)}
              disabled={!requiredByStep()}
            >
              Continuar
            </Button>
          ) : (
            <Button type="button" variant="contained" disabled={loading} onClick={finalizarYCrear}>
              {loading ? "Guardando..." : "Finalizar y crear"}
            </Button>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}
